//
//
//
//
//
//

import { v4 as uuidv4 } from "uuid";
import { lineString, polygon } from "@turf/helpers";
import centerOfMass from "@turf/center-of-mass"
import bbox from "@turf/bbox"
import area from "@turf/area"
import bboxPolygon from "@turf/bbox-polygon"
import { addServerPath } from "@/plugins/add-server-path";

export default {
  name: "SearchMap",
  components: {
  },
  props: {
    initial: {
      type: Object,
      required: true,
    },
    typeViewPlacemark: {
      type: String,
      required: true,
      default: 'circle'
    }
  },
  data() {
    return {
      id: null,
      instance: null,
      clusterer: null,
      geoCollection: null,
      ready: false,
      loading: false,
    }
  },
  computed: {
    globalGeometry() {
      return Object.values(this.initial).map(x => {
        x.uuid = uuidv4()

        switch (x.type) {
        case "polygon":
          const polygonGeometry = polygon(x.geometry)
          const bboxPolygonGeometry = bbox(polygonGeometry)
          x['center-of-mass'] = centerOfMass(polygonGeometry).geometry.coordinates
          x['bounds'] = [
            [bboxPolygonGeometry[0], bboxPolygonGeometry[1]],
            [bboxPolygonGeometry[2], bboxPolygonGeometry[3]],
          ]
          break;
        case "polyline":
          const lineStringGeometry = lineString(x.geometry)
          const bboxLineStringGeometry = bbox(lineStringGeometry)
          x['center-of-mass'] = centerOfMass(lineStringGeometry).geometry.coordinates
          x['bounds'] = [
            [bboxLineStringGeometry[0], bboxLineStringGeometry[1]],
            [bboxLineStringGeometry[2], bboxLineStringGeometry[3]],
          ]
          break;
        case "circle":
          x['center-of-mass'] = x.geometry
          break;
        }

        return x
      })
    }
  },
  mounted() {
    this.ready = true
    this.id = uuidv4()
    this.$refs["map-frame"].setAttribute('id', this.id)

    // eslint-disable-next-line nuxt/no-env-in-hooks
    if (process.client) {
      window.addEventListener('ymap-init', this.readyMapHandler)

      if (window.mapObjectInit === undefined) {
        window.mapObjectInit = () => {
          window.dispatchEvent(new CustomEvent('ymap-init'))
        }
      }

      if (!window.ymaps) {
        const observer = new window.IntersectionObserver(([entry]) => {
          if (entry.isIntersecting && !this.loading && !window.addedYmaps) {
            this.loading = true
            const elem = document.createElement('script');
            elem.type = 'text/javascript';
            elem.src = '//api-maps.yandex.ru/2.1/?apikey=34a52aa6-9e5b-4c91-8246-ebe300263a6f&lang=ru_RU&onload=mapObjectInit';
            document.getElementsByTagName('body')[0].appendChild(elem);
            window.addedYmaps = true
          }
        }, {
          root: null,
          threshold: 0
        })

        observer.observe(this.$refs["map-frame"]);

      } else {
        this.init()
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener('ymap-init', this.readyMapHandler)
  },
  methods: {
    setBounds() {
      const bounds = Object.values(this.initial)
        .map(item => {
          return item.bounds
        })
        .flat()

      this.instance.setBounds(ymaps.util.bounds.fromPoints(bounds), {
        checkZoomRange: true,
        zoomMargin: 35
      }).then(() => {
        const zoom = this.instance.getZoom()

        if (zoom > 17) {
          this.instance.setZoom(17)
        }
      })
    },
    buildGeoObject(type = 'circle', coordinates = [], _uuid = "", object) {
      const circleOptions = {
        iconLayout: 'default#image',
        iconImageHref: require('@/assets/svg/location.svg'),
        iconImageSize: [36, 36],
        iconColor: '#328fe5',
      }
      const polylineOptions = {
        editorDrawingCursor: "crosshair",
        editorMinPoints: 2,
        fillColor: 'rgba(50, 143, 229, 0.75)',
        strokeColor: '#328FE6',
        strokeWidth: 2
      }
      const polygonOptions = {
        editorDrawingCursor: "crosshair",
        editorMinPoints: 3,
        fillColor: 'rgba(50, 143, 229, 0.5)',
        strokeColor: '#328FE6',
        strokeWidth: 1
      }
      const geoObjectUuid = _uuid
      let geoObject = null

      switch (type) {
      case "polygon":
        geoObject = new ymaps.Polygon(coordinates, {
          uuid: geoObjectUuid,
        }, polygonOptions);
        geoObject.properties.set('center-of-mass', centerOfMass(polygon(coordinates)))
        break;
      case "polyline":
        geoObject = new ymaps.Polyline(coordinates, {
          uuid: geoObjectUuid,
        }, polylineOptions);
        geoObject.properties.set('center-of-mass', centerOfMass(lineString(coordinates)))
        break;
      case "circle":
        geoObject = new ymaps.Placemark(coordinates, {
          uuid: geoObjectUuid,
        }, circleOptions);

        if(this.typeViewPlacemark != 'circle'){
          let imgUrl = '';

          switch (this.typeViewPlacemark) {
          case 'type':
            imgUrl = object.type.icon ? addServerPath(`/admin/icon_files/${object.type.icon}/picto_black/`) : '';
            break;
          case 'photo':
            imgUrl = object.icon ? addServerPath(`/admin/icon_files/${object.icon}/picto_black/`) : '';
            break;
          }

          if(imgUrl.length) {
            let customImage = new Image();
            customImage.crossOrigin = "Anonymous";
            customImage.onload = function () {

              let canvas = document.createElement('canvas');
              let size = 36;
              canvas.width = size;
              canvas.height = size;
              let ctx = canvas.getContext('2d');

              ctx.beginPath();
              ctx.arc(canvas.width / 2, canvas.height / 2, (canvas.width / 2) - 1, 0, 2 * Math.PI, false);
              ctx.fillStyle = 'white';
              ctx.fill();
              ctx.lineWidth = 1;
              ctx.strokeStyle = 'gray';
              ctx.stroke();
              ctx.closePath();

              let imgWidth = customImage.width;
              let imgHeight = customImage.height;
              let scaleFactor = ((Math.max(canvas.width, canvas.height) / 2) - 1) * 2 / Math.max(imgWidth, imgHeight) / 1.5;
              let newWidth = imgWidth * scaleFactor;
              let newHeight = imgHeight * scaleFactor;
              let xPos = (canvas.width / 2) - newWidth / 2;
              let yPos = (canvas.height / 2) - newHeight / 2;

              ctx.save();
              ctx.beginPath();
              ctx.arc((canvas.width / 2), (canvas.height / 2), ((canvas.width / 2) - 1), 0, 2 * Math.PI, false);
              ctx.clip();
              ctx.drawImage(customImage, xPos, yPos, newWidth, newHeight);
              ctx.closePath();
              ctx.restore();

              var imageUrl = canvas.toDataURL();
              geoObject.options.set('iconImageSize', [size, size]);
              geoObject.options.set('iconImageHref', imageUrl);
            };

            customImage.src = imgUrl;
          }
        }
        break;
      }

      const imgPath = object.has_image
        ? addServerPath(`/public/object_images_files/first_image/${object.id}/?preview=1`)
        : object.event_icon ? addServerPath(`/admin/icon_files/${object.event_icon}/picto_black/`) : ''

      geoObject.properties.set('balloonContent', `<a class="map-view__baloon-link" href="/objects/${object.slug}">
            ${imgPath && !object.relatedAttrId ?
    `<div class="map-view__baloon-img-wrapper"><img class="map-view__baloon-img${!object.has_image && object.event_icon ? ' map-view__baloon-img--icon' : ''}" src="${imgPath}" width="140" height="105" alt="${object.label}"></div>` : ''}
          <span class="map-view__baloon-label">${object.label}</span>
          <div class="map-view__baloon-text">${object.text}</div>
          </a>`);

      return geoObject
    },
    getArea(geometry) {
      return area(bboxPolygon([...geometry[0], ...geometry[1]]))
    },
    toogleVisibleGeoObject(isShow, geoObject) {
      if(!isShow && this.typeViewPlacemark == 'all'){
        geoObject.options.set('visible', false);
      }
      else {
        geoObject.options.set('visible', true);
      }
    },
    checkZoom() {
      const mapBounds = this.instance.getBounds()
      const mapViewArea = this.getArea(mapBounds)

      this.clusterer.getGeoObjects().forEach(geoObject => {
        const rawData = this.globalGeometry.find(x => x.uuid === geoObject.properties.get('uuid'))

        var isShowPoly = false

        if (['polygon', 'polyline'].includes(rawData.type)) {
          const rawBounds = rawData.bounds

          const needUseSmall = mapViewArea / this.getArea(rawBounds) > 150
          isShowPoly = needUseSmall
          if (needUseSmall && geoObject.geometry.getType() === 'Point'){
            this.toogleVisibleGeoObject(isShowPoly, geoObject);
            return
          }

          if (needUseSmall) {
            this.clusterer.remove(geoObject)
            this.instance.geoObjects.remove(this.clusterer)
          } else {
            this.geoCollection.remove(geoObject)
            this.instance.geoObjects.remove(this.geoCollection)
          }

          let newGeoObject = needUseSmall ?
            this.buildGeoObject('circle', rawData['center-of-mass'], rawData.uuid, rawData.object) :
            this.buildGeoObject(rawData.type, rawData.geometry, rawData.uuid, rawData.object)

          if (needUseSmall) {
            this.clusterer.add(newGeoObject)
            this.instance.geoObjects.add(this.clusterer)
          } else {
            this.geoCollection.add(newGeoObject)
            this.instance.geoObjects.add(this.geoCollection)
          }
        }

        this.toogleVisibleGeoObject(isShowPoly, geoObject);
      })

    },
    init() {
      this.instance = new ymaps.Map(this.id, {
        center: [55.76, 37.64],
        zoom: 7,
        controls: ['zoomControl']
      }, {
        suppressMapOpenBlock: true
      });

      this.clusterer = new ymaps.Clusterer({
        preset: 'islands#invertedBlueClusterIcons',
        hasBalloon: false
      });

      this.geoCollection = new ymaps.GeoObjectCollection();

      this.instance.geoObjects.add(this.clusterer);

      const placemarks = [];
      const polyObjects = [];

      if ('ontouchstart' in window) {
        this.instance.behaviors.disable('drag');
      }

      this.globalGeometry.forEach(item => {
        const geo = item.type === 'circle' && Array.isArray(item.geometry[0])
          ? item.geometry[0][0]
          : item.geometry

        const geoObject = this.buildGeoObject(item.type, geo, item.uuid, item.object)

        if (geoObject instanceof ymaps.Placemark) {
          placemarks.push(geoObject);
        } else {
          polyObjects.push(geoObject);
        }
      })

      if(placemarks.length || polyObjects.length) {
        this.clusterer.add(placemarks).add(polyObjects);
      }

      if(polyObjects.length) {
        for (let i = 0; i < polyObjects.length; i++) {
          this.geoCollection.add(polyObjects[i]);
        }
      }

      this.instance.events.add('boundschange', (event) => {
        if (event.get('newZoom') !== event.get('oldZoom')) {
          this.checkZoom()
        }
      })

      this.setBounds()
    },
    readyMapHandler() {
      this.init()
    }
  }
}
